import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const IMAGES = {
  skyline:
    "https://images.unsplash.com/photo-1623638498061-2fcab5587cb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  craft:
    "https://images.unsplash.com/photo-1611955167811-4711904bb9f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  stone:
    "https://images.unsplash.com/photo-1599707367072-cd6ada2bc375?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const CHAPTERS = [
  {
    num: "01",
    title: "The JLT hub",
    body: "Based in Jumeirah Lakes Towers — Dubai's DMCC diamond district — Sunrays sits at the crossing point of African rough, Indian cutting, and global retail demand. Our desk trades daily with sight-holders, manufacturers, and bourses across three continents.",
    image: IMAGES.skyline,
    alt: "Dubai JLT skyline at dusk",
  },
  {
    num: "02",
    title: "Conflict-free, always",
    body: "Every natural stone we trade is Kimberley Process compliant and screened against sanctioned-origin lists. Provenance is documented before a parcel is ever offered — because trust is the only currency that compounds in this trade.",
    image: IMAGES.craft,
    alt: "Diamond setter at work",
  },
  {
    num: "03",
    title: "Precision over volume",
    body: "We do not push parcels. We match stones to briefs — cut quality, make, fluorescence, and price position checked against the market before we quote. If we would not put it in our own vault, we will not put it in yours.",
    image: IMAGES.stone,
    alt: "Loose certified diamond macro",
  },
];

const ChapterImage = ({ src, alt, num }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <div ref={ref} className="relative aspect-[4/3] overflow-hidden rounded-2xl" data-testid={`about-image-${num}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, scale: 1.18 }}
        className="h-full w-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E10]/70 via-transparent to-transparent" />
      <span className="font-display absolute bottom-4 left-5 text-6xl font-light text-white/15">{num}</span>
    </div>
  );
};

export const AboutManifesto = () => (
  <section id="about" data-testid="about-manifesto" className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32">
    <Reveal>
      <h2 className="font-display max-w-2xl text-3xl font-light leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
        A trading house built on <span className="gold-text italic">three convictions</span>
      </h2>
    </Reveal>

    <div className="mt-20 space-y-24">
      {CHAPTERS.map((c, i) => (
        <div
          key={c.num}
          className={`grid items-center gap-10 lg:grid-cols-2 lg:gap-16 ${i % 2 ? "" : ""}`}
          data-testid={`about-chapter-${c.num}`}
        >
          <Reveal className={i % 2 ? "lg:order-2" : ""}>
            <ChapterImage src={c.image} alt={c.alt} num={c.num} />
          </Reveal>
          <Reveal delay={0.15} className={i % 2 ? "lg:order-1" : ""}>
            <span className="font-display text-sm italic tracking-[0.3em] text-[#C9A24B]">
              Chapter {c.num}
            </span>
            <h3 className="font-display mt-4 text-3xl font-light text-white sm:text-4xl">{c.title}</h3>
            <p className="mt-5 text-base leading-relaxed text-[#B8BCC2]">{c.body}</p>
          </Reveal>
        </div>
      ))}
    </div>
  </section>
);
